/**
 * QUANDO TROCAR AS TELAS (20/10/2026, terceiro conserto do atualizador).
 *
 * O DEFEITO: a troca era deixada ao plugin — `next` + `setMultiDelay([{ kind: 'kill' }])`. O plugin só instala o `next` ao
 * ir para segundo plano e sem condição pendente (`installNext`), e retira a condição "kill" ao abrir de novo. Só que, a
 * cada abertura, o app achava o pacote já baixado e chamava `next` + `setMultiDelay` de NOVO — rearmando a condição. Abrir
 * retirava, o app rearmava, ir para segundo plano achava a condição: nenhum pacote foi aplicado no Mi 9T, nunca (todas as
 * consultas de 20/10 chegaram com as telas embutidas, mesmo depois dos downloads das 17h28 e das 20h29).
 *
 * AGORA QUEM TROCA É O APP, em duas metades:
 *   - o ATUALIZADOR (`AtualizadorDeTelas.tsx`, fora da prova) só BAIXA e ANOTA o pacote "pendente", com a marca da abertura
 *     — esta função decide isso;
 *   - a ABERTURA SEGUINTE aplica, ANTES de desenhar login ou lista (`trocaNaAbertura.ts`, chamada em `main.tsx`), com a tela
 *     "Atualizando…" — nunca com a lista na mão de alguém, nunca com prova guardada no aparelho.
 * Sem `next` e sem condição de atraso: o plugin não troca nada sozinho — nem ao ir para segundo plano.
 */
export type Pendente = { versao: string; abertura: string };
export type Decisao = 'baixar' | 'anotar' | 'esperar';
/** O estado do pacote desta versão no plugin: sem ele; baixando (`downloading`); baixado (`pending`/`success`). */
export type EstadoNoPlugin = 'ausente' | 'baixando' | 'baixado';
/** O download começado, com a marca da abertura que o começou (`canalDoAparelho.ts`, `CHAVE_DO_DOWNLOAD`). */
export type DownloadComecado = { versao: string; abertura: string };

/**
 * O que o ATUALIZADOR faz com o pacote oferecido. UM download por versão (20/10/2026: o …2120 foi baixado 3 vezes em 26 s):
 *   baixando no plugin (o trabalho do Android segue mesmo com o app fechado) → esperar: nunca um segundo download;
 *   ausente, e um download desta versão JÁ começou NESTA abertura         → esperar: no máximo um por abertura;
 *   ausente (nunca baixado, ou interrompido numa abertura anterior)        → baixar: recomeça só numa abertura nova;
 *   baixado, sem anotação ou anotação de outra versão                      → anotar (a próxima abertura aplica);
 *   baixado e anotado                                                      → esperar: quem aplica é a próxima abertura.
 */
export function oQueFazer(p: { versao: string; estado: EstadoNoPlugin; pendente: Pendente | null; download: DownloadComecado | null; aberturaAtual: string }): Decisao {
  if (p.estado === 'baixando') return 'esperar';
  if (p.estado === 'ausente') return p.download && p.download.versao === p.versao && p.download.abertura === p.aberturaAtual ? 'esperar' : 'baixar';
  if (!p.pendente || p.pendente.versao !== p.versao) return 'anotar';
  return 'esperar';
}

/** O estado no plugin a partir do `status` do pacote (`BundleStatus`: downloading, pending, success, error…). */
export function estadoNoPlugin(status: string | undefined): EstadoNoPlugin {
  if (status === 'downloading') return 'baixando';
  if (status === 'pending' || status === 'success') return 'baixado';
  return 'ausente';
}

/** A marca desta abertura: muda a cada vez que o aplicativo (o JavaScript) começa. */
export const ABERTURA_ATUAL = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
